import { PointArray } from '@editor/interfaces/attribute'
import { CuboidAnchor } from '@editor/interfaces/canvas'
import { MaskRegion } from '@editor/interfaces/maskRegion'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

export interface MaskPCDState {
  /**
   * Anchors placed by user to create a cuboid.
   */
  workingMaskCuboid?: CuboidAnchor

  /**
   * Generated cuboid from the anchors.
   */
  workingMask?: MaskRegion

  /**
   * MaskRegion currently being saved.
   */
  savingMask: MaskRegion[]

  /**
   * List of all cuboids loaded.
   */
  regions: MaskRegion[]

  /**
   * Position of current mouse position.
   */
  mouseAnchor?: PointArray

  /**
   * Mask is being saved.
   */
  isSaving: boolean
}

const initialState: MaskPCDState = {
  isSaving: false,
  workingMaskCuboid: undefined,
  workingMask: undefined,
  savingMask: [],
  regions: [],
}

export const slice = createSlice({
  name: 'editor/tools/maskPCD',
  initialState,
  reducers: {
    setIsSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload
    },

    /**
     * Mask Regions
     */
    setMaskRegions: (state, action: PayloadAction<MaskRegion[]>) => {
      state.regions = action.payload
    },
    addMaskRegion: (state, action: PayloadAction<MaskRegion>) => {
      state.regions.push(action.payload)
    },
    removeMaskRegion: (state, action: PayloadAction<string>) => {
      state.regions = state.regions.filter((cuboid) => cuboid.region_id !== action.payload)
    },
    updateMaskRegion: (state, action: PayloadAction<MaskRegion>) => {
      state.regions = state.regions.map((cuboid) =>
        cuboid.region_id === action.payload.region_id ? action.payload : cuboid,
      )
    },

    /**
     * Working Mask
     */
    setMouseAnchor: (state, action: PayloadAction<PointArray | undefined>) => {
      state.mouseAnchor = action.payload
    },
    setWorkingMaskCuboid: (state, action: PayloadAction<CuboidAnchor | undefined>) => {
      state.workingMaskCuboid = action.payload
    },
    addWorkingMaskPoint: (state, { payload }: PayloadAction<PointArray>) => {
      if (!state.workingMaskCuboid) {
        state.workingMaskCuboid = {
          diameter: 0,
          points: [payload],
        }
      } else {
        state.workingMaskCuboid.points.push(payload)
      }
    },
    setWorkingMask: (state, { payload }: PayloadAction<MaskRegion | undefined>) => {
      if (!payload) {
        state.workingMask = undefined
        state.workingMaskCuboid = undefined
        return
      }

      state.workingMask = {
        ...payload,
        region_id: state.workingMask?.region_id || payload.region_id,
      }
    },

    /**
     * Saving Mask
     */
    copySavingMask: (state) => {
      if (!state.workingMask) return

      state.savingMask.push(state.workingMask)
      state.workingMask = undefined
      state.workingMaskCuboid = undefined
    },
    clearSavingMask: (state, { payload }: PayloadAction<string[]>) => {
      state.savingMask = state.savingMask.filter(({ region_id }) => !payload.includes(region_id))
    },

    /**
     * Reset
     */
    resetWorking(state) {
      state.workingMaskCuboid = undefined
      state.workingMask = undefined
      state.mouseAnchor = undefined
    },
  },
})

export const {
  setIsSaving,

  // # Mask Regions
  setMaskRegions,
  addMaskRegion,
  removeMaskRegion,
  updateMaskRegion,

  // # Working
  setMouseAnchor,
  setWorkingMaskCuboid,
  addWorkingMaskPoint,
  setWorkingMask,

  // # Saving
  copySavingMask,
  clearSavingMask,

  // # Reset
  resetWorking,
} = slice.actions

export default slice.reducer
